import { useEffect, useRef } from "react";
import { getCredentials } from "@connect/credentials-storage";
import { isPartnerIntent } from "../../lib/is-partner-intent";
import { devConfigEnabled } from "../../lib/parse-frame-config";
import { VGS_SUBMIT_HEADERS } from "../../lib/vgs/constants";
import type { SubmitOptions, SubmitResult } from "../../lib/vgs/use-vgs-form";
import { vgsToBrand } from "./components/card-fields.constants";
import type { BillingAddress, TokenizedCard } from "./components/card-fields.types";

type VgsSubmit = (path: string, options: SubmitOptions) => Promise<SubmitResult>;

export const FRAMES_CREATE_CARD_TOKEN_PATH = "/platform/v1/frames/payment-methods/tokens";

interface UseCardTokenizationArgs {
  vgsSubmit: VgsSubmit;
  clientToken: string;
}

// Frames_createCardToken OpenAPI rejects null on optional string fields
// (`billingAddress.state must be string`). Drop empty optionals so VGS never
// forwards them.
export function toTokenBillingAddress(address: BillingAddress): Record<string, string> {
  const body: Record<string, string> = {
    street: address.street,
    town: address.town,
    postCode: address.postCode,
    country: address.country ?? "",
  };
  if (address.subStreet) {
    body.subStreet = address.subStreet;
  }
  if (address.state) {
    body.state = address.state;
  }
  return body;
}

export function mapTokenizedCard(response: Record<string, unknown>): TokenizedCard {
  const numberField = (response.number ?? response) as Record<string, unknown>;
  const tokenId =
    (typeof response.id === "string" ? response.id : undefined) ??
    (typeof numberField.token === "string" ? numberField.token : undefined) ??
    "";
  const last4 =
    (typeof response.lastDigits === "string" ? response.lastDigits : undefined) ??
    (typeof numberField.last4 === "string" ? numberField.last4 : undefined) ??
    (typeof numberField.bin === "string" ? numberField.bin.slice(-4) : undefined) ??
    "";
  const brandSource =
    (typeof response.brand === "string" ? response.brand : undefined) ??
    (typeof numberField.cardType === "string" ? numberField.cardType : undefined);
  return { tokenId, last4, brand: vgsToBrand(brandSource) };
}

// Owns the imperative VGS tokenization call. Reads submit through a ref so the
// returned function always sees the latest handle without tearing down.
export function useCardTokenization({ vgsSubmit, clientToken }: UseCardTokenizationArgs) {
  const vgsSubmitRef = useRef(vgsSubmit);
  useEffect(() => {
    vgsSubmitRef.current = vgsSubmit;
  });

  return async (billingAddress?: BillingAddress): Promise<TokenizedCard> => {
    // Local/dev/E2E affordance for environments without a working VGS vault.
    // devConfigEnabled is forced false in a production bundle (DEVXP-806), so a
    // mock token is never minted - let alone sent to create-card - in production.
    const mockToken = (): TokenizedCard => ({
      tokenId: `mock_tok_${Math.random().toString(36).slice(2, 10)}`,
      brand: "unknown",
      last4: "4242",
    });

    if (!clientToken) {
      if (devConfigEnabled) {
        return mockToken();
      }
      throw new Error("Cannot tokenize a card without a clientToken");
    }

    const { customerToken } = await getCredentials();
    const headers: Record<string, string> = {
      ...VGS_SUBMIT_HEADERS,
      "x-client-token": clientToken,
    };
    // partner_intent (enterprise) is clientToken-only. A leftover customerToken
    // from a prior Mission Control login 403s: bearer customer != intent customer.
    const partnerIntent = isPartnerIntent(clientToken);
    if (customerToken && !partnerIntent) {
      headers.Authorization = `Bearer ${customerToken}`;
    }

    const tokenize = vgsSubmitRef
      .current(FRAMES_CREATE_CARD_TOKEN_PATH, {
        method: "POST",
        withCredentials: true,
        headers,
        ...(billingAddress && { data: { billingAddress: toTokenBillingAddress(billingAddress) } }),
      })
      .then(({ status, response }): TokenizedCard => {
        if (status !== 201 || !response) {
          throw new Error(`Tokenization failed (${status})`);
        }
        return mapTokenizedCard(response);
      });

    // In production a tokenization failure surfaces as a real error (handleSubmit
    // maps it to the generic wire error). In dev/E2E fall back to a mock token so
    // the flow is exercisable without a reachable VGS vault.
    if (!devConfigEnabled) {
      return tokenize;
    }
    return tokenize.catch((err: Error): TokenizedCard => {
      // oxlint-disable-next-line no-console -- visibility for dev/E2E debugging
      console.warn("[card-fields] VGS submit failed, using mock token:", err);
      return mockToken();
    });
  };
}
